'use client';

import React, { useState, useEffect, useRef, Suspense, createContext, useContext } from 'react';
import * as THREE from 'three';
import projects from './data/projects';
import translations from './translations';

// ─── Language Context ────────────────────────────────────────────────────────
const LanguageContext = createContext();
const useLanguage = () => useContext(LanguageContext);

// ─── Icons ───────────────────────────────────────────────────────────────────
const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7,10 12,15 17,10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const MailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const MapPinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15,3 21,3 21,9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const GithubIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
  </svg>
);

const LinkedinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

// ─── Navigation ───────────────────────────────────────────────────────────────
const Navigation = ({ activeSection, setActiveSection }) => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home',      label: t.nav.home },
    { id: 'about',     label: t.nav.about },
    { id: 'services',  label: t.nav.services },
    { id: 'skills',    label: t.nav.skills },
    { id: 'education', label: t.nav.education },
    { id: 'projects',  label: t.nav.projects },
    { id: 'community', label: t.nav.community },
    { id: 'contact',   label: t.nav.contact },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(sectionId);
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-700 ${
      scrolled
        ? 'bg-slate-950/90 backdrop-blur-2xl border-b border-slate-700/30 shadow-2xl shadow-slate-900/30'
        : 'bg-transparent'
    }`}>
      <div className="w-full max-w-full mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Badge */}
          <div className="inline-flex items-center space-x-3 text-emerald-400 text-sm font-medium tracking-wide uppercase bg-emerald-400/10 px-4 py-2 rounded-full border border-emerald-400/20">
            <span>{t.nav.badge}</span>
          </div>

          {/* Nav links */}
          <div className="hidden md:flex space-x-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`relative px-4 py-3 rounded-xl font-medium transition-all duration-500 ${
                  activeSection === item.id
                    ? 'text-emerald-300 bg-emerald-400/15 shadow-lg shadow-emerald-400/20'
                    : 'text-slate-300 hover:text-slate-100 hover:bg-slate-700/40'
                } group`}
              >
                {item.label}
                {activeSection === item.id && (
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-400/25 to-teal-400/25 border border-emerald-400/30 animate-pulse" />
                )}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-400/10 to-teal-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            ))}
          </div>

          {/* Language Toggle */}
          <button
            onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
            className="flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-600 text-slate-300 hover:border-emerald-400 hover:text-emerald-400 transition-all duration-300 text-sm font-bold tracking-widest bg-slate-800/40 backdrop-blur-sm"
            title={lang === 'en' ? 'Passer en Français' : 'Switch to English'}
          >
            <span className={lang === 'en' ? 'text-emerald-400' : 'text-slate-500'}>EN</span>
            <span className="text-slate-600 mx-0.5">|</span>
            <span className={lang === 'fr' ? 'text-emerald-400' : 'text-slate-500'}>FR</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

// ─── Three.js Scene ───────────────────────────────────────────────────────────
const ThreeScene = ({ variant = 'geometric' }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;
    const currentMount = mountRef.current;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

    renderer.setSize(300, 300);
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    currentMount.appendChild(renderer.domElement);

    sceneRef.current = scene;
    rendererRef.current = renderer;

    let mesh;

    if (variant === 'geometric') {
      const group = new THREE.Group();
      const geometry = new THREE.IcosahedronGeometry(1, 1);
      const material = new THREE.MeshPhongMaterial({ color: 0x10b981, transparent: true, opacity: 0.8, wireframe: true });
      const mainMesh = new THREE.Mesh(geometry, material);
      group.add(mainMesh);

      for (let i = 0; i < 6; i++) {
        const smallGeometry = new THREE.OctahedronGeometry(0.1);
        const smallMaterial = new THREE.MeshPhongMaterial({ color: 0x14b8a6, transparent: true, opacity: 0.9 });
        const smallMesh = new THREE.Mesh(smallGeometry, smallMaterial);
        const radius = 2;
        const angle = (i / 6) * Math.PI * 2;
        smallMesh.position.set(Math.cos(angle) * radius, Math.sin(angle) * 0.5, Math.sin(angle) * radius);
        group.add(smallMesh);
      }
      mesh = group;
      scene.add(mesh);
    } else if (variant === 'network') {
      const group = new THREE.Group();
      const nodes = [];

      for (let i = 0; i < 8; i++) {
        const nodeGeometry = new THREE.SphereGeometry(0.05, 12, 12);
        const nodeMaterial = new THREE.MeshPhongMaterial({ color: 0x10b981 });
        const node = new THREE.Mesh(nodeGeometry, nodeMaterial);
        node.position.set((Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3);
        nodes.push(node);
        group.add(node);
      }

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          if (Math.random() > 0.6) {
            const points = [nodes[i].position, nodes[j].position];
            const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
            const lineMaterial = new THREE.LineBasicMaterial({ color: 0x14b8a6, transparent: true, opacity: 0.3 });
            const line = new THREE.Line(lineGeometry, lineMaterial);
            group.add(line);
          }
        }
      }
      mesh = group;
      scene.add(mesh);
    }

    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(3, 3, 3);
    directionalLight.castShadow = true;
    const rimLight = new THREE.DirectionalLight(0x14b8a6, 0.3);
    rimLight.position.set(-2, -2, 2);
    scene.add(ambientLight, directionalLight, rimLight);
    camera.position.z = 5;

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      if (mesh) { mesh.rotation.y += 0.005; mesh.rotation.x += 0.002; }
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (currentMount && renderer.domElement) currentMount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, [variant]);

  return <div ref={mountRef} className="w-full h-full flex items-center justify-center" />;
};

// ─── Hero Section ─────────────────────────────────────────────────────────────
const HeroSection = () => {
  const { t } = useLanguage();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: (e.clientX / window.innerWidth) * 2 - 1, y: -(e.clientY / window.innerHeight) * 2 + 1 });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const downloadCV = () => {
    const link = document.createElement('a');
    link.href = '/amine_cv.pdf';
    link.download = 'amine - CV.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadResume = () => {
    window.open('/resume.html', '_blank');
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 w-full">
      <div className="absolute inset-0 w-full">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(20, 184, 166, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(6, 182, 212, 0.1) 0%, transparent 50%)
            `
          }}></div>
        </div>
        <div className="absolute inset-0 opacity-10 animate-pulse" style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)`,
          backgroundSize: '100px 100px'
        }}></div>
      </div>

      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center z-10">
          {/* Content */}
          <div className="flex-1 space-y-8 text-center lg:text-left">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-none">
                <span className="text-slate-100">{t.hero.greeting} </span>
                <div className="inline-block">
                  <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                    Mohamed Amine Sellami
                  </span>
                </div>
              </h1>

              <h2 className="text-lg md:text-xl lg:text-2xl text-slate-400 font-light leading-relaxed">
                {t.hero.subtitle}
              </h2>
            </div>

            <p className="text-slate-300 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {t.hero.desc1}{' '}
              <span className="text-emerald-400 font-semibold">{t.hero.domain1}</span>,{' '}
              <span className="text-emerald-400 font-semibold">{t.hero.domain2}</span>,{' '}
              <span className="text-emerald-400 font-semibold">{t.hero.domain3}</span>,{' '}
              <span className="text-emerald-400 font-semibold">{t.hero.domain4}</span>,{' '}
              <span className="text-emerald-400 font-semibold">{t.hero.domain5}</span>{' '}
              &amp; <span className="text-emerald-400 font-semibold">{t.hero.domain6}</span>.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={downloadCV}
                className="group relative inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 text-white px-8 py-4 rounded-2xl font-semibold text-base hover:shadow-2xl hover:shadow-emerald-500/40 transition-all duration-500 hover:scale-105 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <DownloadIcon />
                <span className="relative z-10">{t.hero.downloadCV}</span>
                <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </button>

              <button
                onClick={downloadResume}
                className="group relative inline-flex items-center justify-center space-x-3 border-2 border-emerald-400 text-emerald-400 px-8 py-4 rounded-2xl font-semibold text-base hover:bg-emerald-400 hover:text-slate-900 transition-all duration-500 overflow-hidden"
              >
                <div className="absolute inset-0 bg-emerald-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                <DownloadIcon className="relative z-10" />
                <span className="relative z-10">{t.hero.downloadResume}</span>
              </button>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4 justify-center lg:justify-start transform -translate-y-4">
              {[
                { icon: LinkedinIcon, href: 'https://www.linkedin.com/in/mohamed-amine-sellami-19686b350/', label: 'LinkedIn' },
                { icon: GithubIcon,   href: 'https://github.com/am11iin', label: 'GitHub' },
                { icon: MailIcon,     href: 'mailto:mohamedsellami479@gmail.com', label: 'Email' },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-14 h-14 bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50 hover:border-emerald-400/50 transition-all duration-500 hover:scale-110 hover:-translate-y-2 backdrop-blur-sm"
                  title={social.label}
                >
                  <social.icon />
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-400/10 to-teal-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </a>
              ))}
            </div>
          </div>

          {/* Profile Image */}
          <div className="flex justify-center relative flex-shrink-0">
            <div
              className="transition-transform duration-500 ease-out"
              style={{ transform: `perspective(1000px) rotateY(${mousePosition.x * 2}deg) rotateX(${-mousePosition.y * 2}deg)` }}
            >
              <div className="relative w-[280px] h-[280px] md:w-[320px] md:h-[320px] lg:w-[350px] lg:h-[350px]">
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-emerald-400 via-teal-400 to-cyan-400 p-1.5">
                  <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
                    <img
                      src="/amine3.jpg"
                      alt="Profile"
                      className="w-[calc(100%-12px)] h-[calc(100%-12px)] rounded-full object-cover object-[10%_50%] shadow-xl"
                    />
                  </div>
                </div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-emerald-400/20 to-teal-400/20 blur-xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── About Section ────────────────────────────────────────────────────────────
const AboutSection = () => {
  const { t, lang } = useLanguage();

  return (
    <section className="pt-20 pb-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 text-emerald-400 text-lg font-bold tracking-wider uppercase bg-emerald-400/15 px-8 py-4 rounded-full border-2 border-emerald-400/30 shadow-lg shadow-emerald-400/20">
            <span>{t.about.sectionLabel}</span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* 3D Animation */}
          <div className="flex justify-center lg:justify-start flex-shrink-0">
            <div className="w-[300px] h-[300px] md:w-[400px] md:h-[400px] relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 rounded-3xl blur-3xl animate-pulse"></div>
              <div className="relative w-full h-full">
                <Suspense fallback={
                  <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl animate-pulse flex items-center justify-center">
                    <div className="w-20 h-20 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                }>
                  <ThreeScene variant="geometric" />
                </Suspense>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-7 text-center lg:text-left">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 leading-tight">
              {t.about.headline1}
              <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                {t.about.headline2}
              </span>
            </h2>

            <div className="space-y-5 text-slate-300 leading-relaxed">
              {/* p1 */}
              <p className="text-base md:text-lg leading-relaxed">{t.about.p1}</p>

              {/* p2 — tech names colored */}
              <p className="text-base md:text-lg leading-relaxed">
                {lang === 'fr' ? (
                  <>Mon stack couvre{' '}
                    <span className="text-emerald-400 font-bold">React</span>,{' '}
                    <span className="text-emerald-400 font-bold">Next.js</span>,{' '}
                    <span className="text-emerald-400 font-bold">TypeScript</span> et{' '}
                    <span className="text-emerald-400 font-bold">Tailwind CSS</span> côté frontend ;{' '}
                    <span className="text-emerald-400 font-bold">Node.js</span>,{' '}
                    <span className="text-emerald-400 font-bold">Express.js</span> et{' '}
                    <span className="text-emerald-400 font-bold">FastAPI</span> côté backend ;{' '}
                    <span className="text-emerald-400 font-bold">PostgreSQL</span> et{' '}
                    <span className="text-emerald-400 font-bold">Firebase</span> pour les données.
                    Côté IA,{' '}
                    <span className="text-emerald-400 font-bold">Python</span>,{' '}
                    <span className="text-emerald-400 font-bold">PyTorch</span> et{' '}
                    <span className="text-emerald-400 font-bold">OpenCV</span> pour la vision par ordinateur.
                  </>
                ) : (
                  <>My stack spans{' '}
                    <span className="text-emerald-400 font-bold">React</span>,{' '}
                    <span className="text-emerald-400 font-bold">Next.js</span>,{' '}
                    <span className="text-emerald-400 font-bold">TypeScript</span> and{' '}
                    <span className="text-emerald-400 font-bold">Tailwind CSS</span> on the frontend;{' '}
                    <span className="text-emerald-400 font-bold">Node.js</span>,{' '}
                    <span className="text-emerald-400 font-bold">Express.js</span> and{' '}
                    <span className="text-emerald-400 font-bold">FastAPI</span> on the backend;{' '}
                    <span className="text-emerald-400 font-bold">PostgreSQL</span> and{' '}
                    <span className="text-emerald-400 font-bold">Firebase</span> for data.
                    On the AI side,{' '}
                    <span className="text-emerald-400 font-bold">Python</span>,{' '}
                    <span className="text-emerald-400 font-bold">PyTorch</span> and{' '}
                    <span className="text-emerald-400 font-bold">OpenCV</span> for computer vision.
                  </>
                )}
              </p>

              {/* p3 */}
              <p className="text-base md:text-lg leading-relaxed">{t.about.p3}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-slate-700/50">
              {[
                { number: '5+', label: t.about.stat1, color: 'from-emerald-400 to-teal-400' },
                { number: '2+', label: t.about.stat2, color: 'from-teal-400 to-cyan-400' },
                { number: '6+', label: t.about.stat3, color: 'from-cyan-400 to-blue-400' },
              ].map((stat) => (
                <div key={stat.label} className="text-center group">
                  <div className={`text-3xl md:text-4xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-500`}>
                    {stat.number}
                  </div>
                  <div className="text-slate-400 text-sm md:text-base font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


// ─── Services Section ─────────────────────────────────────────────────────────
const ServicesSection = () => {
  const { t } = useLanguage();

  return (
    <section className="pt-20 pb-20 bg-slate-900 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 text-emerald-400 text-lg font-bold tracking-wider uppercase bg-emerald-400/15 px-8 py-4 rounded-full border-2 border-emerald-400/30 shadow-lg shadow-emerald-400/20">
            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
            <span>{t.services.sectionLabel}</span>
          </div>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-4 leading-tight">
            {t.services.headline.split(' ').slice(0, -1).join(' ')}{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              {t.services.headline.split(' ').slice(-1)[0]}
            </span>
          </h2>
          <p className="text-slate-400 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            {t.services.subheadline}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.items.map((service, index) => (
            <div
              key={service.title}
              className="group bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-400/50 transition-all duration-700 hover:bg-slate-800/50 backdrop-blur-sm hover:scale-105"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-all duration-500 shadow-lg`}>
                {service.icon}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${service.color} opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-500`}></div>
              </div>

              <h3 className="text-2xl font-bold text-slate-100 mb-4 group-hover:text-emerald-400 transition-colors duration-500">
                {service.title}
              </h3>

              <p className="text-slate-400 mb-6 leading-relaxed">{service.description}</p>

              <div className="space-y-3 mb-8">
                {service.features.map((feature, i) => (
                  <div key={feature} className="flex items-center space-x-3 group-hover:translate-x-2 transition-transform duration-300" style={{ transitionDelay: `${i * 50}ms` }}>
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <span className="text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>

              <button className="w-full bg-transparent border-2 border-slate-600 text-slate-300 py-3 rounded-2xl font-semibold hover:border-emerald-400 hover:text-emerald-400 hover:bg-emerald-400/5 transition-all duration-500">
                {t.services.learnMore}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Skills Section ───────────────────────────────────────────────────────────
const SkillsSection = () => {
  const { t } = useLanguage();

  const skillCategories = [
    {
      ...t.skills.categories.ai,
      featured: true,
      layout: 'lg:col-span-7 lg:row-span-2',
      accent: 'bg-orange-400',
      line: 'via-orange-400',
      halo: 'ring-orange-400/15',
      glow: 'from-orange-500/15 via-amber-500/5 to-transparent',
      skills: ['OpenCV', 'PyTorch', 'MediaPipe', 'Scikit-learn', 'CNN', 'SVM', 'PCA', 'Machine Learning'],
    },
    {
      ...t.skills.categories.languages,
      layout: 'lg:col-span-5',
      accent: 'bg-violet-400',
      line: 'via-violet-400',
      halo: 'ring-violet-400/15',
      glow: 'from-violet-500/10 to-transparent',
      skills: ['Python', 'JavaScript', 'C', 'C#'],
    },
    {
      ...t.skills.categories.frontend,
      layout: 'lg:col-span-5',
      accent: 'bg-cyan-400',
      line: 'via-cyan-400',
      halo: 'ring-cyan-400/15',
      glow: 'from-cyan-500/10 to-transparent',
      skills: ['React', 'HTML5', 'CSS3', 'Tailwind CSS', 'Vite'],
    },
    {
      ...t.skills.categories.backend,
      layout: 'lg:col-span-6',
      accent: 'bg-emerald-400',
      line: 'via-emerald-400',
      halo: 'ring-emerald-400/15',
      glow: 'from-emerald-500/10 to-transparent',
      skills: ['FastAPI', 'REST API', 'PostgreSQL', 'Database Design'],
    },
    {
      ...t.skills.categories.tools,
      layout: 'lg:col-span-6',
      accent: 'bg-blue-400',
      line: 'via-blue-400',
      halo: 'ring-blue-400/15',
      glow: 'from-blue-500/10 to-transparent',
      skills: ['Git', 'GitHub', 'Linux', 'Jupyter Notebook', 'Figma', 'Flex', 'Bison'],
    },
  ];

  return (
    <section className="pt-20 pb-20 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 text-emerald-400 text-lg font-bold tracking-wider uppercase bg-emerald-400/15 px-8 py-4 rounded-full border-2 border-emerald-400/30 shadow-lg shadow-emerald-400/20">
            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
            <span>{t.skills.sectionLabel}</span>
          </div>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-3 leading-tight">
            {t.skills.headline1}{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              {t.skills.headline2}
            </span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
            {t.skills.subheadline}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:auto-rows-fr">
          {skillCategories.map((category) => (
            <article
              key={category.title}
              className={`group relative overflow-hidden rounded-3xl border border-slate-800/80 bg-slate-900/60 p-6 md:p-7 shadow-lg shadow-slate-950/20 transition-all duration-300 hover:-translate-y-1 hover:border-slate-600/80 hover:shadow-2xl hover:shadow-slate-950/40 ${category.layout} ${category.featured ? 'lg:min-h-[360px]' : 'min-h-[205px]'}`}
            >
              <div aria-hidden="true" className={`absolute inset-0 bg-gradient-to-br ${category.glow} opacity-70 transition-opacity duration-300 group-hover:opacity-100`} />
              <div aria-hidden="true" className={`absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent ${category.line} to-transparent opacity-70`} />

              <div className="relative z-10 flex h-full flex-col">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${category.accent} ring-4 ${category.halo}`} />
                    <h3 className={`${category.featured ? 'text-xl md:text-2xl' : 'text-lg'} font-bold tracking-tight text-slate-100`}>
                      {category.title}
                    </h3>
                  </div>
                  <span className="rounded-full border border-slate-700/70 bg-slate-950/40 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-slate-500">
                    {String(category.skills.length).padStart(2, '0')}
                  </span>
                </div>

                <p className={`mb-6 max-w-xl text-sm leading-relaxed text-slate-400 ${category.featured ? 'md:text-base' : ''}`}>
                  {category.description}
                </p>

                <div className={`mt-auto flex flex-wrap gap-2 ${category.featured ? 'max-w-xl' : ''}`}>
                  {category.skills.map(skill => (
                    <span
                      key={skill}
                      className="rounded-xl border border-slate-700/70 bg-slate-950/45 px-3 py-1.5 text-sm font-medium text-slate-300 transition-colors duration-200 hover:border-slate-500 hover:bg-slate-800 hover:text-white"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Education Section ────────────────────────────────────────────────────────
const EducationSection = () => {
  const { t, lang } = useLanguage();

  const educationData = lang === 'fr' ? [
    {
      degree: 'Master en Vision par Ordinateur',
      institution: 'USTHB — Université des Sciences et de la Technologie Houari-Boumédiène',
      location: 'Alger, Algérie',
      period: 'Sept. 2025 – Présent',
      isCurrent: true,
      description: 'Formation spécialisée en vision par ordinateur et en intelligence artificielle.',
      courses: ['Vision par Ordinateur', 'Machine Learning', "Traitement d’Image"],
      color: 'from-orange-500 to-amber-500',
      dot: 'bg-orange-400',
    },
    {
      degree: 'Licence en Informatique',
      institution: 'USTHB — Université des Sciences et de la Technologie Houari-Boumédiène',
      location: 'Alger, Algérie',
      period: 'Sept. 2022 – Juin 2025',
      isCurrent: false,
      description: 'Diplôme en informatique avec une formation en développement logiciel et en bases de données.',
      courses: ['Génie Logiciel', 'Bases de Données', 'Compilation'],
      color: 'from-emerald-500 to-teal-500',
      dot: 'bg-emerald-400',
    },
  ] : [
    {
      degree: "Master's Degree in Computer Vision",
      institution: 'USTHB — University of Science and Technology Houari Boumediene',
      location: 'Algiers, Algeria',
      period: 'Sep. 2025 – Present',
      isCurrent: true,
      description: 'Specialized studies in computer vision and artificial intelligence.',
      courses: ['Computer Vision', 'Machine Learning', 'Image Processing'],
      color: 'from-orange-500 to-amber-500',
      dot: 'bg-orange-400',
    },
    {
      degree: "Bachelor's Degree in Computer Science",
      institution: 'USTHB — University of Science and Technology Houari Boumediene',
      location: 'Algiers, Algeria',
      period: 'Sep. 2022 – Jun. 2025',
      isCurrent: false,
      description: 'Computer science degree covering software development and databases.',
      courses: ['Software Engineering', 'Databases', 'Compiler Design'],
      color: 'from-emerald-500 to-teal-500',
      dot: 'bg-emerald-400',
    },
  ];

  return (
    <section className="pt-20 pb-20 bg-slate-900 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        {/* Section Label */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 text-emerald-400 text-lg font-bold tracking-wider uppercase bg-emerald-400/15 px-8 py-4 rounded-full border-2 border-emerald-400/30 shadow-lg shadow-emerald-400/20">
            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
            <span>{t.education.sectionLabel}</span>
          </div>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-4 leading-tight">
            {t.education.headline1}{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">{t.education.headline2}</span>
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-emerald-400/60 via-teal-400/40 to-transparent md:-translate-x-px" />

          <div className="space-y-12">
            {educationData.map((edu, index) => (
              <div key={index} className={`relative flex flex-col md:flex-row gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                {/* Dot on timeline */}
                <div className="absolute left-6 md:left-1/2 top-8 w-4 h-4 rounded-full border-2 border-slate-900 shadow-lg md:-translate-x-2 z-10"
                  style={{ background: edu.dot.replace('bg-', '').includes('orange') ? '#fb923c' : '#34d399' }}
                />

                {/* Period badge — desktop only, on opposite side */}
                <div className={`hidden md:flex w-1/2 items-start pt-6 ${index % 2 === 0 ? 'justify-end pr-12' : 'justify-start pl-12'}`}>
                  <div className="flex flex-col items-center gap-2">
                    <span className={`px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r ${edu.color} text-white shadow-lg`}>
                      {edu.period}
                    </span>
                    {edu.isCurrent && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-400/20 text-emerald-400 border border-emerald-400/30 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse inline-block" />
                        {t.education.current}
                      </span>
                    )}
                  </div>
                </div>

                {/* Card */}
                <div className={`w-full md:w-1/2 ml-14 md:ml-0 ${index % 2 === 0 ? 'md:pl-12' : 'md:pr-12'}`}>
                  <div className="bg-slate-800/40 border border-slate-700/50 rounded-3xl p-7 hover:border-emerald-400/40 hover:bg-slate-800/60 transition-all duration-500 group">
                    {/* Mobile period */}
                    <div className="flex items-center gap-3 mb-4 md:hidden">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${edu.color} text-white`}>
                        {edu.period}
                      </span>
                      {edu.isCurrent && (
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-400/20 text-emerald-400 border border-emerald-400/30">
                          {t.education.current}
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg md:text-xl font-bold text-slate-100 mb-1 leading-snug group-hover:text-emerald-300 transition-colors duration-300">
                      {edu.degree}
                    </h3>
                    <p className="text-emerald-400 font-semibold text-sm mb-1">{edu.institution}</p>
                    <p className="text-slate-500 text-xs mb-4">{edu.location}</p>
                    <p className="text-slate-400 text-sm leading-relaxed mb-5">{edu.description}</p>

                    {/* Coursework */}
                    <div>
                      <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-3">{t.education.coursework}</p>
                      <div className="flex flex-wrap gap-2">
                        {edu.courses.map((course) => (
                          <span key={course} className="px-3 py-1 bg-slate-700/60 rounded-full text-xs text-slate-300 border border-slate-600/50 hover:border-emerald-400/40 hover:text-emerald-300 transition-all duration-300">
                            {course}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Projects Section ─────────────────────────────────────────────────────────
const ProjectsSection = () => {
  const { t } = useLanguage();

  return (
    <section className="pt-20 pb-20 bg-slate-950 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 text-emerald-400 text-lg font-bold tracking-wider uppercase bg-emerald-400/15 px-8 py-4 rounded-full border-2 border-emerald-400/30 shadow-lg shadow-emerald-400/20">
            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
            <span>{t.projects.sectionLabel}</span>
          </div>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-4 leading-tight">
            {t.projects.headline1}{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">{t.projects.headline2}</span>
          </h2>
          <p className="text-slate-400 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            {t.projects.subheadline}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={project.title}
              className="group bg-slate-900/30 border border-slate-800/50 rounded-3xl overflow-hidden hover:border-emerald-400/50 transition-all duration-700 hover:bg-slate-900/50 backdrop-blur-sm hover:scale-105 flex flex-col"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative aspect-video overflow-hidden bg-slate-800">
                <img
                  src={project.image}
                  alt={`${project.title} preview`}
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent opacity-60"></div>
              </div>

              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-xl font-bold text-slate-100 mb-4 group-hover:text-emerald-400 transition-colors duration-500">
                  {project.title}
                </h3>
                <p className="text-slate-400 mb-6 leading-relaxed text-sm flex-1">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.map((technology, i) => (
                    <span
                      key={technology}
                      className="px-3 py-1 bg-emerald-400/10 text-emerald-400 rounded-full text-xs border border-emerald-400/20 hover:bg-emerald-400/20 transition-all duration-300"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      {technology}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={project.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-3 px-3 rounded-2xl font-semibold text-sm hover:shadow-lg hover:shadow-emerald-500/30 transition-all duration-500"
                  >
                    <span>{t.projects.liveDemo}</span>
                    <ExternalLinkIcon />
                  </a>
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-slate-800/50 border-2 border-slate-700 text-slate-300 py-3 px-3 rounded-2xl font-semibold text-sm hover:border-emerald-400 hover:text-emerald-400 hover:bg-emerald-400/5 transition-all duration-500"
                  >
                    <span>{t.projects.githubRepo}</span>
                    <GithubIcon />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Community Section ────────────────────────────────────────────────────────
const CommunitySection = () => {
  const { t } = useLanguage();

  const linksMeta = [
    { icon: GithubIcon,   url: 'https://github.com/am11iin', color: 'from-slate-700 to-slate-900',  hoverColor: 'hover:border-slate-500' },
    { icon: LinkedinIcon, url: 'https://www.linkedin.com/in/mohamed-amine-sellami-19686b350/', color: 'from-blue-600 to-blue-800',   hoverColor: 'hover:border-blue-400' },
    { icon: MailIcon,     url: 'mailto:mohamedsellami479@gmail.com', color: 'from-emerald-500 to-teal-700', hoverColor: 'hover:border-emerald-400' },
  ];

  return (
    <section className="pt-20 pb-20 bg-slate-950 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 text-emerald-400 text-lg font-bold tracking-wider uppercase bg-emerald-400/15 px-8 py-4 rounded-full border-2 border-emerald-400/30 shadow-lg shadow-emerald-400/20">
            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
            <span>{t.community.sectionLabel}</span>
          </div>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-4 leading-tight">
            {t.community.headline1}{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">{t.community.headline2}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {t.community.links.map((link, index) => {
            const meta = linksMeta[index];
            return (
              <a
                key={link.title}
                href={meta.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`group bg-slate-900/50 border border-slate-800/80 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 ${meta.hoverColor} backdrop-blur-sm relative overflow-hidden`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${meta.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                <div className="w-16 h-16 bg-slate-800/80 rounded-2xl flex items-center justify-center text-slate-300 mb-6 group-hover:scale-110 group-hover:text-white transition-all duration-500 shadow-lg relative z-10 border border-slate-700/50 group-hover:border-transparent">
                  <meta.icon />
                </div>
                <h3 className="text-2xl font-bold text-slate-100 mb-3 group-hover:text-white transition-colors duration-300 relative z-10">
                  {link.title}
                </h3>
                <p className="text-slate-400 leading-relaxed relative z-10 group-hover:text-slate-300 transition-colors duration-300">
                  {link.description}
                </p>
                <div className="mt-6 flex items-center text-emerald-400 font-semibold text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 relative z-10">
                  <span>{t.community.visit} {link.title}</span>
                  <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Contact Section ──────────────────────────────────────────────────────────
const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (typeof window !== 'undefined' && window.emailjs) {
        window.emailjs.init('dTtVGCNwX9Sgh0xZ_');
        const result = await window.emailjs.send(
          'service_x7t5fjk',
          'template_6gusals',
          { from_name: formData.name, from_email: formData.email, message: formData.message, to_name: 'sellami mohamed amine' }
        );
        console.log('Email sent successfully:', result);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error('EmailJS not loaded');
      }
    } catch (error) {
      console.error('Email send failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="pt-20 pb-20 bg-slate-900 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 text-emerald-400 text-lg font-bold tracking-wider uppercase bg-emerald-400/15 px-8 py-4 rounded-full border-2 border-emerald-400/30 shadow-lg shadow-emerald-400/20">
            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
            <span>{t.contact.sectionLabel}</span>
          </div>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-4 leading-tight">
            {t.contact.headline1}{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">{t.contact.headline2}</span>
          </h2>
          <p className="text-slate-400 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            {t.contact.subheadline}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-16">
          {/* Contact Info */}
          <div className="flex-1 space-y-8">
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-100 mb-6 leading-tight">
                {t.contact.sideHeadline1} <span className="text-emerald-400">{t.contact.sideHeadline2}</span>
              </h3>
              <p className="text-slate-400 leading-relaxed mb-8 text-lg">{t.contact.sideP}</p>
            </div>

            <div className="space-y-6">
              {[
                { icon: MailIcon,    label: t.contact.labelEmail,    value: 'Mohamedsellami479@gmail.com' },
                { icon: PhoneIcon,   label: t.contact.labelPhone,    value: '+213 784129273' },
                { icon: MapPinIcon,  label: t.contact.labelLocation, value: 'Algiers, Algeria.' },
              ].map((contact) => (
                <div key={contact.label} className="flex items-center space-x-4 group hover:translate-x-2 transition-transform duration-300">
                  <div className="w-12 h-12 bg-emerald-400/10 border border-emerald-400/20 rounded-2xl flex items-center justify-center text-emerald-400 group-hover:scale-110 group-hover:bg-emerald-400/20 transition-all duration-300">
                    <contact.icon />
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">{contact.label}</p>
                    <p className="text-slate-100 font-semibold">{contact.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="w-64 h-64 mx-auto lg:mx-0">
              <Suspense fallback={
                <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl animate-pulse flex items-center justify-center">
                  <div className="w-16 h-16 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              }>
                <ThreeScene variant="network" />
              </Suspense>
            </div>
          </div>

          {/* Contact Form */}
          <div className="flex-1 bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-slate-100 mb-2 font-semibold">{t.contact.formName}</label>
                <input
                  type="text" name="name" value={formData.name} onChange={handleChange}
                  required disabled={isSubmitting} placeholder={t.contact.formNamePh}
                  className="w-full px-4 py-3 rounded-2xl bg-slate-900/50 text-slate-100 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all duration-500 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-slate-100 mb-2 font-semibold">{t.contact.formEmail}</label>
                <input
                  type="email" name="email" value={formData.email} onChange={handleChange}
                  required disabled={isSubmitting} placeholder={t.contact.formEmailPh}
                  className="w-full px-4 py-3 rounded-2xl bg-slate-900/50 text-slate-100 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all duration-500 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-slate-100 mb-2 font-semibold">{t.contact.formMessage}</label>
                <textarea
                  name="message" value={formData.message} onChange={handleChange}
                  required disabled={isSubmitting} rows="5" placeholder={t.contact.formMessagePh}
                  className="w-full px-4 py-3 rounded-2xl bg-slate-900/50 text-slate-100 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all duration-500 resize-none backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-emerald-400/10 border border-emerald-400/20 rounded-2xl text-emerald-400 text-center">
                  {t.contact.success}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-400/10 border border-red-400/20 rounded-2xl text-red-400 text-center">
                  {t.contact.error}
                </div>
              )}

              <button
                type="submit" disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 text-white py-4 rounded-2xl font-bold hover:shadow-2xl hover:shadow-emerald-500/40 transition-all duration-500 hover:scale-105 relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span className="relative z-10">{isSubmitting ? t.contact.sending : t.contact.send}</span>
                {isSubmitting && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Main Portfolio ───────────────────────────────────────────────────────────
const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [lang, setLang] = useState('en');
  const t = translations[lang];

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    script.async = true;
    script.onload = () => { if (window.emailjs) window.emailjs.init('service_x7t5fjk'); };
    document.head.appendChild(script);
    return () => { if (document.head.contains(script)) document.head.removeChild(script); };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'skills', 'education', 'projects', 'community', 'contact'];
      const scrollPosition = window.scrollY + 100;
      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update html lang attribute
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="w-full max-w-full bg-slate-950 text-slate-100 overflow-x-hidden">
        <Navigation activeSection={activeSection} setActiveSection={setActiveSection} />

        <div id="home"><HeroSection /></div>
        <div id="about"><AboutSection /></div>
        <div id="services"><ServicesSection /></div>
        <div id="skills"><SkillsSection /></div>
        <div id="education"><EducationSection /></div>
        <div id="projects"><ProjectsSection /></div>
        <div id="community"><CommunitySection /></div>
        <div id="contact"><ContactSection /></div>

        <footer className="bg-slate-950 py-12 w-full">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-slate-400">{t.footer.copyright}</div>
              <div className="flex space-x-6">
                {[
                  { icon: LinkedinIcon, href: 'https://www.linkedin.com/in/mohamed-amine-sellami-19686b350/', label: 'LinkedIn' },
                  { icon: GithubIcon,   href: 'https://github.com/am11iin', label: 'GitHub' },
                  { icon: MailIcon,     href: 'mailto:Mohamedsellami479@gmail.com', label: 'Email' },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-emerald-400 transition-all duration-500 hover:scale-125"
                  >
                    <social.icon />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </LanguageContext.Provider>
  );
};

export default Portfolio;
